import axios from "axios";
import pako from 'pako';
import '../css/Main/Main.css';
import { validateToken } from "../utils/Cookie";
import { useNavigate } from 'react-router-dom';
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import TableItens from "../components/TableItens";
import { ICliente, IProduto, IProdutoNaoElegivel } from "../interfaces/IGeradora";
import { mask_cgc } from "../utils/format";
import {
  Alert,
  Button,
  Container,
  debounce,
  Snackbar,
} from "@mui/material";
import SessionContext from "../Context/SessionContext";
import { IControlPageDatagrid, IFiltro, IReqExcel, IReqProduto } from "../interfaces/IGlobal";
import { useSocketTasks, cancelTask, SocketTask } from "./../hooks/useTasksSocket";
import { socket, connectSocket, disconnectSocket } from "../socket";
import ModalExportExcel from "../components/ModalExportExcel";
import ModalGerarModelo from "../components/ModalGerarModelo";
import ModalExportarEstoqueGarantido from "../components/ModalExportarEstoqueGarantido";
import FiltrosGeracao from "../components/FiltrosGeracao";

interface IFindClientBy {
  value: string;
  description: string;
}

interface IRespnseGetClient {
  clients: ICliente[];
}

export default function Main() {
  const { session } = useContext(SessionContext);
  const groups = session.groups;
  const azureId = localStorage.getItem("azureId") || '';
  const navigate = useNavigate();

  // Estados de UI
  const [modalOpen, setModalOpen] = useState(false);
  const [messageProgress, setMessageProgress] = useState('');
  const [typeModal, setTypeModal] = useState<'export' | 'busca'>('busca');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [typeMessage, setTypeMessage] = useState<'info' | 'error' | 'warning'>('info');

  // Estados de Dados e Busca
  const [discount, setDiscount] = useState('');
  const [codeProduct, setCodeProduct] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [clickNewVersion, setClickNewVersion] = useState(false);
  const [itensGeradora, setItensGeradora] = useState<IProduto[]>([]);
  const [produtosNaoElegiveis, setProdutosNaoElegiveis] = useState<IProdutoNaoElegivel[]>([]);

  // Estados de Cliente
  const [busca_por, set_busca_por] = useState<IFindClientBy | null>({ value: 'todos', description: 'Código/Cnpj/Nome' });
  const [clientList, setClientList] = useState<ICliente[]>([]);
  const [inputClient, setInputClient] = useState('');
  const [client, setClient] = useState<ICliente>();
  const [loadingFindClient, setLoadingFindClient] = useState(false);

  // Estados de Grid/Paginação
  const [controlPage, setControlPage] = useState<IControlPageDatagrid>({ page: 0, limit: 25 });
  const [rowCount, setRowCount] = useState(0);
  const [tipoGrid, setTipoGrid] = useState<'padrao' | 'estoque' | 'cta'>('padrao');

  // Estados de Modais
  const [openPadrao, setOpenPadrao] = useState(false);
  const [openEstoque, setOpenEstoque] = useState(false);
  const [openCTA, setOpenCTA] = useState(false);
  const [openExportOptions, setOpenExportOptions] = useState(false);

  const [currentTaskName, setCurrentTaskName] = useState<'busca_produto' | 'gerar_excel' | 'gerar_pdf'>('busca_produto');

  const [filtro, setFiltro] = useState<IFiltro>({
    promocao: [],
    onHold: [],
    categoria: [],
    material: [],
    marca: [],
    estoque: '=',
    qtdEstoque: '',
    regimeTributario: 3,
  });

  // --- Callbacks de Socket ---
  const handleCancelar = useCallback((origin: 'export' | 'busca') => {
    if (!azureId) return;
    const jobCancel = origin === 'busca' ? 'cancelar_busca_produto' : 'cancelar_geracao_excel';
    cancelTask(socket, azureId, jobCancel);
  }, [azureId]);

  const onTaskStatusChange = useCallback((jobId: string, status: string) => {
    const jobExcelId = `gerar_excel_${azureId}`;
    const jobSearchId = `busca_produto_${azureId}`;
    if (jobId === jobExcelId && status === 'em_andamento' && !modalOpen) handleCancelar('export');
    if (jobId === jobSearchId && status === 'em_andamento' && !modalOpen) handleCancelar('busca');
  }, [azureId, modalOpen, handleCancelar]);

  const taskGerarExcel = useCallback((data: any) => {
    if (data.status === 500) {
      setTypeMessage('error');
      setSnackbarMessage(data.message);
      setOpenSnackbar(true);
    } else {
      if (data.url) {
        const link = document.createElement("a");
        link.href = data.url;
        link.target = "_blank";
        link.click();
      } else if (data.excelBlob) {
        const blob = new Blob([new Uint8Array(data.excelBlob)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "Planilha Geradora.xlsx";
        link.click();
      }
    }
    setModalOpen(false);
    setMessageProgress('');
  }, []);

  const taskGerarPdf = useCallback((data: any) => {
    if (data.status === 500) {
      setTypeMessage("error");
      setSnackbarMessage(data.message);
      setOpenSnackbar(true);
    } else {
      if (data.url) {
        const link = document.createElement("a");
        link.href = data.url;
        link.target = "_blank";
        link.click();
      } else if (data.pdfBlob) {
        const blob = new Blob([new Uint8Array(data.pdfBlob)], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "Relatorio.pdf";
        link.click();
      }
    }
    setModalOpen(false);
    setMessageProgress("");
  }, []);

  const tasks: SocketTask[] = useMemo(() => {
    const baseTasks = {
      exportando: (data: any) => setMessageProgress(data.message),
    };

    if (currentTaskName === 'gerar_excel') {
      return [{ id: azureId, name: 'gerar_excel', events: { ...baseTasks, finish_gerar_excel: taskGerarExcel }, cancelEventName: "cancelar_geracao_excel" }];
    }
    if (currentTaskName === "gerar_pdf") {
      return [{ id: azureId, name: "gerar_pdf", events: { ...baseTasks, finish_gerar_pdf: taskGerarPdf }, cancelEventName: "cancelar_geracao_pdf" }];
    }
    return [{
      id: azureId,
      name: 'busca_produto',
      events: {
        consultando: (data: any) => setMessageProgress(data.message),
        finish_busca_produto: (data: any) => {
          if (data.status === 200) {
            const productFormated = data?.produtos?.map((item: IProduto, index: number) => ({ ...item, id: index + 1 })) || [];
            setItensGeradora(productFormated);
            setProdutosNaoElegiveis(data?.produtosNaoElegiveis || []);
            if (data?.produtosNaoElegiveis?.length > 0) {
              setTypeMessage('warning');
              setSnackbarMessage(`${data.produtosNaoElegiveis.length} Produtos com cadastro incompleto.`);
              setOpenSnackbar(true);
            }
          } else {
            setTypeMessage('error');
            setSnackbarMessage(data.message);
            setOpenSnackbar(true);
          }
          setModalOpen(false);
        }
      },
      cancelEventName: "cancelar_busca_produto",
    }];
  }, [azureId, currentTaskName, taskGerarExcel, taskGerarPdf]);

  useSocketTasks(socket, tasks, onTaskStatusChange);

  // --- Lógica de Busca ---
  const getItensGeradora = useCallback(async (versao: string, page: number, limit: number, total: number, clickNewVersion: boolean, modelo: string) => {
    const codPros = codeProduct.split(/[\s,]+/).filter((item) => item !== '');
    const isFiltroPreenchido = filtro.promocao.length > 0 || filtro.material.length > 0 || filtro.qtdEstoque !== "";

    if (!isFiltroPreenchido && codPros.length === 0) {
      setSnackbarMessage("Por favor, preencha ao menos um filtro ou insira itens.");
      setTypeMessage('warning');
      setOpenSnackbar(true);
      return;
    }

    setIsLoading(true);
    setClickNewVersion(clickNewVersion);
    setCurrentTaskName('busca_produto');
    setTypeModal('busca');
    setModalOpen(true);

    socket.emit("busca_produto", {
      azureId, modelo, codEmp: Number(localStorage.getItem("empresa")),
      codPro: codPros, descontoDigitado: String(discount?.replace(',', '.') || '0'),
      versao, filtro, page: page + 1, total, limit,
    });
    setIsLoading(false);
  }, [azureId, codeProduct, discount, filtro]);

  // --- Handlers de Modelos ---
  const handleGerarModeloPadrao = (opt: any) => {
    setOpenPadrao(false); setTipoGrid('padrao');
    getItensGeradora(opt, controlPage.page, controlPage.limit, rowCount, opt === 'btnGerarOnline', 'padrao');
  };

  const handleGerarModeloEstoque = (opt: any) => {
    if (filtro.qtdEstoque === '' && codeProduct === '') {
      setTypeMessage('error'); setSnackbarMessage('Informe a quantidade do estoque.'); setOpenSnackbar(true);
      return;
    }
    setOpenEstoque(false); setTipoGrid('estoque');
    getItensGeradora(opt, controlPage.page, controlPage.limit, rowCount, opt === 'btnGerarOnline', 'estoque');
  };

  const handleGerarModeloCTA = (opt: any) => {
    setOpenCTA(false); setTipoGrid('cta');
    getItensGeradora(opt, controlPage.page, controlPage.limit, rowCount, opt === 'btnGerarOnline', 'cta');
  };

  // --- Exportação ---
  const handleExport = (tipo: 'excel' | 'pdf') => {
    const payload: IReqExcel = {
      produtosNaoElegiveis, itens: itensGeradora, sigUfs: filtro.sigUfs || client?.sigUfs || '',
      versao: clickNewVersion ? "btnGerarOnline" : "btnGerarLocal",
      azureId, modelo: tipoGrid,
      userHasPermission: groups?.filter(g => g.displayName.includes('Espelho')) || []
    };
    const compressed = pako.deflate(JSON.stringify(payload));
    setCurrentTaskName(tipo === 'excel' ? 'gerar_excel' : 'gerar_pdf');
    socket.emit(tipo === 'excel' ? "gerar_excel" : "gerar_pdf", compressed);
    setTypeModal('export'); setModalOpen(true);
  };

  // --- Busca de Cliente ---
  const handleSearchClient = useCallback(debounce(async (value: string, findBy: any) => {
    if (!value || value.length < 2) { setClientList([]); return; }
    try {
      setLoadingFindClient(true);
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND}/clients`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: { cod_emp: Number(localStorage.getItem("empresa")), search: value, find_by: findBy?.value || 'todos' }
      });
      setClientList(Array.isArray(data) ? data : data.clients ?? []);
    } catch { setClientList([]); } finally { setLoadingFindClient(false); }
  }, 500), []);

  useEffect(() => { handleSearchClient(inputClient, busca_por); }, [inputClient, busca_por, handleSearchClient]);

  useEffect(() => {
    if (!validateToken()) navigate('/login?logout=true');
    connectSocket();
    return () => disconnectSocket();
  }, [navigate]);

  return (
    <>
      <Container maxWidth="xl">
        <FiltrosGeracao
          filtro={filtro} setFiltro={setFiltro}
          busca_por={busca_por} set_busca_por={set_busca_por}
          clientList={clientList} client={client}
          inputClient={inputClient} setInputClient={setInputClient}
          handleChangeClient={(cli) => { setClient(cli); setFiltro(p => ({ ...p, sigUfs: cli?.sigUfs, cgcCpfCliente: cli?.cgcCpf })); }}
          loadingFindClient={loadingFindClient}
          mask_cgc={mask_cgc}
          handleDisabledFieldClick={(msg) => { setTypeMessage('error'); setSnackbarMessage(msg); setOpenSnackbar(true); }}
          discount={discount} setDiscount={setDiscount}
          codeProduct={codeProduct} setCodeProduct={setCodeProduct}
          onEnterCodeProduct={() => setOpenPadrao(true)}
        />

        <div className="buttonsDiv">
          <Button style={{ textTransform: 'none', backgroundColor: '#1A3661' }} variant="contained" onClick={() => setOpenPadrao(true)}>Gerar Modelo Padrão</Button>

          <ModalGerarModelo open={openPadrao} onClose={() => setOpenPadrao(false)} titulo="Gerar Modelo Padrão" onConfirm={handleGerarModeloPadrao} />

          {itensGeradora.length > 0 && (
            <Button
              style={{ textTransform: 'none', backgroundColor: '#1A3661' }}
              variant="contained"
              onClick={() => tipoGrid === 'estoque' ? setOpenExportOptions(true) : handleExport('excel')}
            >
              {tipoGrid === 'estoque' ? 'Exportar' : 'Gerar Excel'}
            </Button>
          )}

          <ModalExportExcel open={modalOpen} messageExport={messageProgress} handleCancelar={handleCancelar} origin={typeModal} />
        </div>

        <TableItens
          itens={itensGeradora} loading={isLoading}
          cliente={client as ICliente} sigUfs={filtro.sigUfs} groups={groups}
          controlPage={controlPage} setControlPage={setControlPage} tipoGrid={tipoGrid} />
      </Container>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={typeMessage} sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </>
  );
}
